import { useState, useEffect, useCallback, useMemo } from 'react';
import type { Inspection } from '../types';
import { inspectionRepository } from '../data/inspectionRepository';

/**
 * useInspections フックの戻り値
 */
interface UseInspectionsReturn {
  /** 全データ */
  data: Inspection[];
  /** 進行中の点検一覧 */
  ongoingInspections: Inspection[];
  /** 完了した点検一覧 */
  completedInspections: Inspection[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * 点検一覧を取得するフック
 */
export const useInspections = (homeId?: string): UseInspectionsReturn => {
  const [data, setData] = useState<Inspection[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await inspectionRepository.getAll(homeId || '1');
      if (result.success) {
        setData(result.data);
      } else {
        throw new Error(result.error.message);
      }
    } catch (e) {
      setError(e as Error);
    } finally {
      setIsLoading(false);
    }
  }, [homeId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const ongoingInspections = useMemo(
    () => data.filter((item) => item.status !== 'completed'),
    [data]
  );

  const completedInspections = useMemo(
    () => data.filter((item) => item.status === 'completed'),
    [data]
  );

  return {
    data,
    ongoingInspections,
    completedInspections,
    isLoading,
    error,
    refetch: fetchData,
  };
};
